import type { useForm } from "react-hook-form"

import { useSearchNavigateFilter } from "../../../../shared/hooks/useSearchNavigateFilter"
import { WAREHOUSE_SHELVING_BIN_STATUS_FILTERS, WAREHOUSE_SHELVING_FILTERS_DEFAULT } from "../constants"
import { useWarehouseShelvingSearchNavigate } from "../hooks/useWarehouseShelvingSearchNavigate"
import type { WarehouseShelvingFilterChipsList } from "../types"
import { getWarehouseShelvingActiveBinStatusChips, isWarehouseShelvingFilterEmpty } from "../utils"

import { BIN_SIZE_LABELS } from "@/constants/warehouse"
import type { WarehouseShelvingBinSize, WarehouseShelvingListFilter } from "@/graphql/codegen/graphql"

interface UseWarehouseShelvingFilterReturn {
  formMethods: ReturnType<typeof useForm<WarehouseShelvingListFilter>>
  isFilterEmpty: () => boolean
  clearBinStatus: () => void
  getFilterChips: () => WarehouseShelvingFilterChipsList | null
  changeBinStatus: (status: string) => void
  utils: {
    clearFilterAll: () => void
    clearFilterField: (name: keyof WarehouseShelvingListFilter, nestedValueToRemove?: string | number) => void
    enableFilterIfChecked: (name: string, checked: boolean | null | undefined) => void
    changeFilter: (name: string, value: any) => void
    changeFilters: (items: Partial<WarehouseShelvingListFilter>) => void
  }
}

export const useWarehouseShelvingFilter = (): UseWarehouseShelvingFilterReturn => {
  const { currentNavigate, currentSearch, formMethods } = useWarehouseShelvingSearchNavigate()
  const { clearFilterAll, clearFilterField, enableFilterIfChecked, changeFilter, changeFilters } =
    useSearchNavigateFilter<WarehouseShelvingListFilter>({
      filtersDefaultValue: WAREHOUSE_SHELVING_FILTERS_DEFAULT,
      currentNavigate,
    })

  const isFilterEmpty = (): boolean =>
    isWarehouseShelvingFilterEmpty(
      currentSearch.coldStorage,
      currentSearch.h3ColdStorage,
      currentSearch.deep,
      currentSearch.binSize,
      currentSearch.isAvailable,
      currentSearch.isDoNotUse,
      currentSearch.isUndefined
    )

  // returns labels for chips based on current search params
  const getFilterChips = () => {
    if (isFilterEmpty()) {
      return null
    }

    return {
      labelColdStorage: currentSearch.coldStorage ? "Cold Storage" : "",
      labelH3ColdStorage: currentSearch.h3ColdStorage ? "H3 Cold Storage" : "",
      labelDeep: currentSearch.deep ? "Deep" : "",
      binSize:
        currentSearch.binSize?.length && currentSearch.binSize?.length > 0
          ? currentSearch.binSize.map((size: WarehouseShelvingBinSize) => ({
              name: size,
              label: BIN_SIZE_LABELS[size],
            }))
          : "",
      labelBinStatus: getWarehouseShelvingActiveBinStatusChips(currentSearch.isAvailable, currentSearch.isDoNotUse),
      labelUndefined: currentSearch.isUndefined ? "Has Undefined values" : "",
    } as WarehouseShelvingFilterChipsList
  }

  const clearBinStatus = () => {
    changeFilters({
      isAvailable: undefined,
      isDoNotUse: undefined,
    })
  }

  const changeBinStatus = (status: string) => {
    const filters = WAREHOUSE_SHELVING_BIN_STATUS_FILTERS[status]
    if (filters) {
      changeFilters(filters)
    }
  }

  return {
    formMethods,
    utils: {
      clearFilterAll,
      clearFilterField,
      enableFilterIfChecked,
      changeFilter,
      changeFilters,
    },

    isFilterEmpty,
    getFilterChips,
    clearBinStatus,
    changeBinStatus,
  }
}
